import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Integration } from 'services/integration-service';

export interface IntegrationsStoreState {
  integrations: Integration[];
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  
  // Actions
  setIntegrations: (integrations: Integration[]) => void;
  addIntegration: (integration: Integration) => void;
  removeIntegration: (connectionId: string) => void;
  updateIntegration: (connectionId: string, updates: Partial<Integration>) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  
  // Helper methods
  getIntegrationByConnectionId: (connectionId: string) => Integration | undefined;
  getIntegrationByType: (integrationType: string) => Integration | undefined;
  getActiveIntegrations: () => Integration[];
  isConnected: (integrationType: string) => boolean;
}

const initialState = {
  integrations: [],
  isLoading: false,
  error: null,
  lastFetchTime: null,
};

export const useIntegrationsStore = create<IntegrationsStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setIntegrations: (integrations) =>
        set({
          integrations,
          lastFetchTime: Date.now(),
          error: null,
        }),
        
      addIntegration: (integration) =>
        set((state) => ({
          integrations: [...state.integrations, integration],
          lastFetchTime: Date.now(),
        })),
        
      removeIntegration: (connectionId) =>
        set((state) => ({
          integrations: state.integrations.filter(
            (integration) => integration.connection_id !== connectionId
          ),
          lastFetchTime: Date.now(),
        })),
        
      updateIntegration: (connectionId, updates) =>
        set((state) => ({
          integrations: state.integrations.map((integration) =>
            integration.connection_id === connectionId
              ? { ...integration, ...updates }
              : integration
          ),
          lastFetchTime: Date.now(),
        })),
        
      setLoading: (isLoading) => set({ isLoading }),
      
      setError: (error) => set({ error, isLoading: false }),
      
      reset: () => set(initialState),
      
      // Helper methods
      getIntegrationByConnectionId: (connectionId) => {
        const state = get();
        return state.integrations.find(
          (integration) => integration.connection_id === connectionId
        );
      },
      
      getIntegrationByType: (integrationType) => {
        const state = get();
        return state.integrations.find(
          (integration) => 
            integration.integration_type === integrationType && 
            integration.status === 'active'
        );
      },
      
      getActiveIntegrations: () => {
        const state = get();
        return state.integrations.filter(
          (integration) => integration.status === 'active'
        );
      },
      
      isConnected: (integrationType) => {
        const state = get();
        return state.integrations.some(
          (integration) => 
            integration.integration_type === integrationType && 
            integration.status === 'active'
        );
      },
    }),
    {
      name: 'integrations-storage',
      partialize: (state) => ({
        integrations: state.integrations,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);
